import React, { useState } from 'react';
import {
  Box,
  Typography,
  Slider,
  TextField,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Collapse,
  IconButton,
  Divider,
  Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  getHotelStarRating,
  getHotelGuestScoreForFilter,
  REVIEW_SCORE_MIN,
} from '../../lib/hotelFilterUtils';
import { NATIONALITY_OPTIONS } from '../../lib/currency';
import './FiltersSidebar.css';

const FilterSection = ({ title, children, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box className="filter-section">
      <Box 
        className="filter-section__header"
        onClick={() => setExpanded(!expanded)}
      >
        <Typography className="filter-section__title">{title}</Typography>
        <IconButton size="small" className="filter-section__toggle">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box className="filter-section__content">
          {children}
        </Box>
      </Collapse>
      <Divider className="filter-section__divider" />
    </Box>
  );
};

const FilterCheckbox = ({ label, count, checked, onChange, id }) => (
  <FormControlLabel
    control={
      <Checkbox
        checked={checked}
        onChange={onChange}
        id={id}
        className="filter-checkbox"
      />
    }
    label={
      <Box className="filter-checkbox__label">
        <span>{label}</span>
        {count !== undefined && <span className="filter-checkbox__count">({count})</span>}
      </Box>
    }
    className="filter-checkbox-wrapper"
  />
);

const FiltersSidebar = ({ filters, onFilterChange, hotels = [] }) => {
  const DEFAULT_MAX_PRICE = 20000;

  // Calculate counts for filters based on actual hotel data
  const getCount = (filterType, value) => {
    if (!hotels || hotels.length === 0) return 0;

    switch (filterType) {
      case 'propertyType':
        if (value === 'hotel') {
          return hotels.filter(h => {
            const name = (h.name || '').toLowerCase();
            const desc = ((h.description || h.overview || '') + '').toLowerCase();
            const combined = `${name} ${desc}`;
            const apartmentSignals =
              /apartment|aparthotel|self-catering|self catering|kitchenette|rental unit|serviced apartment/i.test(
                combined
              );
            const hotelSignals =
              /hotel|\binns?\b|resort|lodge|hostel|suites?\b|palace|marriott|hilton|hyatt|sheraton|holiday inn|ibis|mercure|novotel|pullman|westin|wyndham|radisson|intercontinental|sofitel|melia|crowne/i.test(
                combined
              );
            if (apartmentSignals && !hotelSignals) return false;
            return true;
          }).length;
        }
        if (value === 'apartment') {
          return hotels.filter(h => {
            const name = (h.name || '').toLowerCase();
            const desc = ((h.description || h.overview || '') + '').toLowerCase();
            const combined = `${name} ${desc}`;
            return /apartment|aparthotel|self-catering|self catering|kitchenette|rental unit|serviced apartment/i.test(
              combined
            );
          }).length;
        }
        return 0;

      case 'starRating':
        return hotels.filter(h => getHotelStarRating(h) === value).length;

      case 'reviewScore':
        {
          const min = REVIEW_SCORE_MIN[value];
          if (typeof min !== 'number') return 0;
          return hotels.filter((h) => {
            const score = getHotelGuestScoreForFilter(h);
            return score != null && score >= min;
          }).length;
        }

      case 'facilities':
      case 'roomFacilities':
      case 'landmarks':
      case 'bedPreference':
      case 'meals':
      case 'neighborhood':
      case 'brands':
      case 'travelGroup':
      case 'distance':
      case 'funThings':
      case 'certifications':
      case 'propertyAccessibility':
      case 'roomAccessibility':
        // For now, return a reasonable estimate based on total hotels
        // These would need more specific data from API
        return Math.floor(hotels.length * 0.3);

      default:
        return 0;
    }
  };

  const hasActiveFilters = () => {
    return filters.priceRange[0] > 0 || 
           filters.priceRange[1] < DEFAULT_MAX_PRICE ||
           filters.propertyType.length > 0 ||
           filters.starRating.length > 0 ||
           filters.reviewScore.length > 0 ||
           Object.values(filters.amenities || {}).some(v => v) ||
           Object.values(filters.facilities || {}).some(v => v) ||
           Object.values(filters.roomFacilities || {}).some(v => v) ||
           (filters.brands && filters.brands.length > 0) ||
           (filters.meals && filters.meals.length > 0) ||
           (filters.landmarks && filters.landmarks.length > 0) ||
           (filters.neighborhood && filters.neighborhood.length > 0);
  };

  const clearAllFilters = () => {
    onFilterChange({
      priceRange: [0, DEFAULT_MAX_PRICE],
      nationality: filters.nationality || 'GB',
      propertyType: [],
      starRating: [],
      reviewScore: [],
      amenities: {},
      facilities: {},
      landmarks: [],
      bedPreference: [],
      meals: [],
      neighborhood: [],
      brands: [],
      travelGroup: [],
      distance: [],
      funThings: [],
      certifications: [],
      propertyAccessibility: {},
      roomAccessibility: {},
      roomFacilities: {},
    });
  };

  return (
    <aside className="filters-sidebar">
      <Box className="filters-sidebar__header">
        <Typography className="filters-sidebar__title">Filter by:</Typography>
        {hasActiveFilters() && (
          <Button 
            className="filters-sidebar__clear"
            onClick={clearAllFilters}
          >
            Clear all
          </Button>
        )}
      </Box>

      {/* Your Budget */}
      <FilterSection title="Your budget (per night)" defaultExpanded={true}>
        <Box className="filter-price">
          <Slider
            value={filters.priceRange || [0, DEFAULT_MAX_PRICE]}
            onChange={(e, newValue) => 
              onFilterChange({ ...filters, priceRange: newValue })
            }
            min={0}
            max={DEFAULT_MAX_PRICE}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `£${value}`}
            className="filter-price__slider"
          />
          <Box className="filter-price__range">
            <span>£{filters.priceRange?.[0] || 0}</span>
            <span>–</span>
            <span>£{filters.priceRange?.[1] || DEFAULT_MAX_PRICE}+</span>
          </Box>
        </Box>
      </FilterSection>

      <FilterSection title="Traveller nationality" defaultExpanded={true}>
        <TextField
          select
          size="small"
          fullWidth
          value={filters.nationality || 'GB'}
          onChange={(e) => onFilterChange({ ...filters, nationality: e.target.value })}
          className="filters-sidebar__nationality"
        >
          {NATIONALITY_OPTIONS.map((opt) => (
            <MenuItem key={opt.code} value={opt.code}>
              {opt.label} ({opt.code})
            </MenuItem>
          ))}
        </TextField>
      </FilterSection>

      {/* Popular Filters */}
      <FilterSection title="Popular filters" defaultExpanded={true}>
        <FormGroup>
          <FilterCheckbox
            label="Hotels"
            count={getCount('propertyType', 'hotel')}
            checked={filters.propertyType?.includes('hotel') || false}
            onChange={(e) => {
              const propertyType = filters.propertyType || [];
              const newTypes = e.target.checked
                ? [...propertyType, 'hotel']
                : propertyType.filter(t => t !== 'hotel');
              onFilterChange({ ...filters, propertyType: newTypes });
            }}
            id="filter-hotel"
          />
          <FilterCheckbox
            label="Apartments"
            count={getCount('propertyType', 'apartment')}
            checked={filters.propertyType?.includes('apartment') || false}
            onChange={(e) => {
              const propertyType = filters.propertyType || [];
              const newTypes = e.target.checked
                ? [...propertyType, 'apartment']
                : propertyType.filter(t => t !== 'apartment');
              onFilterChange({ ...filters, propertyType: newTypes });
            }}
            id="filter-apartment"
          />
          <FilterCheckbox
            label="Very Good: 8+"
            count={getCount('reviewScore', 'veryGood')}
            checked={filters.reviewScore?.includes('veryGood') || false}
            onChange={(e) => {
              const reviewScore = filters.reviewScore || [];
              const newScores = e.target.checked
                ? [...reviewScore, 'veryGood']
                : reviewScore.filter(s => s !== 'veryGood');
              onFilterChange({ ...filters, reviewScore: newScores });
            }}
            id="filter-very-good"
          />
          <FilterCheckbox
            label="4 stars"
            count={getCount('starRating', 4)}
            checked={filters.starRating?.includes(4) || false}
            onChange={(e) => {
              const starRating = filters.starRating || [];
              const newRatings = e.target.checked
                ? [...starRating, 4]
                : starRating.filter(r => r !== 4);
              onFilterChange({ ...filters, starRating: newRatings });
            }}
            id="filter-4-star"
          />
          <FilterCheckbox
            label="Private bathroom"
            count={getCount('facilities', 'privateBathroom')}
            checked={filters.facilities?.privateBathroom || false}
            onChange={(e) => {
              onFilterChange({
                ...filters,
                facilities: {
                  ...filters.facilities,
                  privateBathroom: e.target.checked
                }
              });
            }}
            id="filter-private-bathroom"
          />
        </FormGroup>
      </FilterSection>

      {/* Landmarks */}
      <FilterSection title="Landmarks (Near hotel)" defaultExpanded={false}>
        <FormGroup>
          {['Tower of London', 'London Eye', 'London Bridge', 'Paddington Station', 'South Harrow Tube Station'].map(landmark => (
            <FilterCheckbox
              key={landmark}
              label={landmark}
              count={getCount('landmarks', landmark)}
              checked={filters.landmarks?.includes(landmark) || false}
              onChange={(e) => {
                const landmarks = filters.landmarks || [];
                const newLandmarks = e.target.checked
                  ? [...landmarks, landmark]
                  : landmarks.filter(l => l !== landmark);
                onFilterChange({ ...filters, landmarks: newLandmarks });
              }}
              id={`filter-landmark-${landmark}`}
            />
          ))}
        </FormGroup>
      </FilterSection>

      {/* Bed Preference */}
      <FilterSection title="Bed preference" defaultExpanded={false}>
        <FormGroup>
          <FilterCheckbox
            label="Double bed"
            count={getCount('bedPreference', 'double')}
            checked={filters.bedPreference?.includes('double') || false}
            onChange={(e) => {
              const bedPreference = filters.bedPreference || [];
              const newBeds = e.target.checked
                ? [...bedPreference, 'double']
                : bedPreference.filter(b => b !== 'double');
              onFilterChange({ ...filters, bedPreference: newBeds });
            }}
            id="filter-double-bed"
          />
          <FilterCheckbox
            label="Twin beds"
            count={getCount('bedPreference', 'twin')}
            checked={filters.bedPreference?.includes('twin') || false}
            onChange={(e) => {
              const bedPreference = filters.bedPreference || [];
              const newBeds = e.target.checked
                ? [...bedPreference, 'twin']
                : bedPreference.filter(b => b !== 'twin');
              onFilterChange({ ...filters, bedPreference: newBeds });
            }}
            id="filter-twin-beds"
          />
        </FormGroup>
      </FilterSection>

      {/* Meals */}
      <FilterSection title="Meals" defaultExpanded={false}>
        <FormGroup>
          <FilterCheckbox
            label="Breakfast included"
            count={getCount('meals', 'breakfast')}
            checked={filters.meals?.includes('breakfast') || false}
            onChange={(e) => {
              const meals = filters.meals || [];
              const newMeals = e.target.checked
                ? [...meals, 'breakfast']
                : meals.filter(m => m !== 'breakfast');
              onFilterChange({ ...filters, meals: newMeals });
            }}
            id="filter-breakfast"
          />
          <FilterCheckbox
            label="Breakfast & dinner included"
            count={getCount('meals', 'breakfastDinner')}
            checked={filters.meals?.includes('breakfastDinner') || false}
            onChange={(e) => {
              const meals = filters.meals || [];
              const newMeals = e.target.checked
                ? [...meals, 'breakfastDinner']
                : meals.filter(m => m !== 'breakfastDinner');
              onFilterChange({ ...filters, meals: newMeals });
            }}
            id="filter-breakfast-dinner"
          />
          <FilterCheckbox
            label="Kitchen facilities"
            count={getCount('meals', 'kitchen')}
            checked={filters.meals?.includes('kitchen') || false}
            onChange={(e) => {
              const meals = filters.meals || [];
              const newMeals = e.target.checked
                ? [...meals, 'kitchen']
                : meals.filter(m => m !== 'kitchen');
              onFilterChange({ ...filters, meals: newMeals });
            }}
            id="filter-kitchen"
          />
        </FormGroup>
      </FilterSection>

      {/* Property Rating */}
      <FilterSection title="Property rating (Stars)" defaultExpanded={false}>
        <FormGroup>
          {[5, 4, 3, 2, 1].map(stars => (
            <FilterCheckbox
              key={stars}
              label={`${stars} star${stars > 1 ? 's' : ''}`}
              count={getCount('starRating', stars)}
              checked={filters.starRating?.includes(stars) || false}
              onChange={(e) => {
                const starRating = filters.starRating || [];
                const newRatings = e.target.checked
                  ? [...starRating, stars]
                  : starRating.filter(r => r !== stars);
                onFilterChange({ ...filters, starRating: newRatings });
              }}
              id={`filter-${stars}-star`}
            />
          ))}
        </FormGroup>
      </FilterSection>

      {/* Neighborhood */}
      <FilterSection title="Neighborhood" defaultExpanded={false}>
        <FormGroup>
          {['Central London', 'Heathrow Airport area', 'Westminster Borough', 'Kensington and Chelsea', 'Tower Hamlets', 'Camden', 'Hyde Park', 'West End', 'Kensington', 'Southwark'].map(neighborhood => (
            <FilterCheckbox
              key={neighborhood}
              label={neighborhood}
              count={getCount('neighborhood', neighborhood)}
              checked={filters.neighborhood?.includes(neighborhood) || false}
              onChange={(e) => {
                const neighborhoodList = filters.neighborhood || [];
                const newNeighborhoods = e.target.checked
                  ? [...neighborhoodList, neighborhood]
                  : neighborhoodList.filter(n => n !== neighborhood);
                onFilterChange({ ...filters, neighborhood: newNeighborhoods });
              }}
              id={`filter-neighborhood-${neighborhood}`}
            />
          ))}
        </FormGroup>
      </FilterSection>

      {/* Facilities */}
      <FilterSection title="Facilities" defaultExpanded={false}>
        <FormGroup>
          {[
            { key: 'hotTub', label: 'Hot tub/Jacuzzi' },
            { key: 'spa', label: 'Spa' },
            { key: 'swimmingPool', label: 'Swimming pool' },
            { key: 'parking', label: 'Parking' },
            { key: 'fitnessCenter', label: 'Fitness center' }
          ].map(facility => (
            <FilterCheckbox
              key={facility.key}
              label={facility.label}
              count={getCount('facilities', facility.key)}
              checked={filters.facilities?.[facility.key] || false}
              onChange={(e) => {
                onFilterChange({
                  ...filters,
                  facilities: {
                    ...filters.facilities,
                    [facility.key]: e.target.checked
                  }
                });
              }}
              id={`filter-${facility.key}`}
            />
          ))}
        </FormGroup>
      </FilterSection>

      {/* Room Facilities */}
      <FilterSection title="Room facilities" defaultExpanded={false}>
        <FormGroup>
          {[
            { key: 'privateBathroom', label: 'Private bathroom' },
            { key: 'kitchen', label: 'Kitchen/Kitchenette' },
            { key: 'balcony', label: 'Balcony' },
            { key: 'airConditioning', label: 'Air conditioning' },
            { key: 'privatePool', label: 'Private pool' }
          ].map(facility => (
            <FilterCheckbox
              key={facility.key}
              label={facility.label}
              count={getCount('roomFacilities', facility.key)}
              checked={filters.roomFacilities?.[facility.key] || false}
              onChange={(e) => {
                onFilterChange({
                  ...filters,
                  roomFacilities: {
                    ...filters.roomFacilities,
                    [facility.key]: e.target.checked
                  }
                });
              }}
              id={`filter-room-${facility.key}`}
            />
          ))}
        </FormGroup>
      </FilterSection>

      {/* Review Score */}
      <FilterSection title="Review score" defaultExpanded={false}>
        <FormGroup>
          {[
            { key: 'wonderful', label: 'Wonderful: 9+', min: 9 },
            { key: 'veryGood', label: 'Very Good: 8+', min: 8 },
            { key: 'good', label: 'Good: 7+', min: 7 },
            { key: 'pleasant', label: 'Pleasant: 6+', min: 6 }
          ].map(score => (
            <FilterCheckbox
              key={score.key}
              label={score.label}
              count={getCount('reviewScore', score.key)}
              checked={filters.reviewScore?.includes(score.key) || false}
              onChange={(e) => {
                const reviewScore = filters.reviewScore || [];
                const newScores = e.target.checked
                  ? [...reviewScore, score.key]
                  : reviewScore.filter(s => s !== score.key);
                onFilterChange({ ...filters, reviewScore: newScores });
              }}
              id={`filter-review-${score.key}`}
            />
          ))}
        </FormGroup>
      </FilterSection>

      {/* Brands */}
      <FilterSection title="Brands" defaultExpanded={false}>
        <FormGroup>
          {['OYO Rooms', 'Mornington Collection', 'Z Hotels', 'Park Plaza Hotels & Resorts', 'Radisson Blu', 'Ibis'].map(brand => (
            <FilterCheckbox
              key={brand}
              label={brand}
              count={getCount('brands', brand)}
              checked={filters.brands?.includes(brand) || false}
              onChange={(e) => {
                const brands = filters.brands || [];
                const newBrands = e.target.checked
                  ? [...brands, brand]
                  : brands.filter(b => b !== brand);
                onFilterChange({ ...filters, brands: newBrands });
              }}
              id={`filter-brand-${brand}`}
            />
          ))}
        </FormGroup>
      </FilterSection>

      {/* Travel Group */}
      <FilterSection title="Travel group" defaultExpanded={false}>
        <FormGroup>
          {[
            { key: 'petFriendly', label: 'Pet friendly' },
            { key: 'adultsOnly', label: 'Adults only' },
            { key: 'lgbtqFriendly', label: 'Travel Proud (LGBTQ+ friendly)' }
          ].map(group => (
            <FilterCheckbox
              key={group.key}
              label={group.label}
              count={getCount('travelGroup', group.key)}
              checked={filters.travelGroup?.includes(group.key) || false}
              onChange={(e) => {
                const travelGroup = filters.travelGroup || [];
                const newGroups = e.target.checked
                  ? [...travelGroup, group.key]
                  : travelGroup.filter(g => g !== group.key);
                onFilterChange({ ...filters, travelGroup: newGroups });
              }}
              id={`filter-travel-${group.key}`}
            />
          ))}
        </FormGroup>
      </FilterSection>

      {/* Distance from Center */}
      <FilterSection title="Distance from center" defaultExpanded={false}>
        <FormGroup>
          {[
            { key: 'halfMile', label: 'Less than 1/2 mile' },
            { key: 'oneMile', label: 'Less than 1 mile' },
            { key: 'twoMiles', label: 'Less than 2 miles' }
          ].map(distance => (
            <FilterCheckbox
              key={distance.key}
              label={distance.label}
              count={getCount('distance', distance.key)}
              checked={filters.distance?.includes(distance.key) || false}
              onChange={(e) => {
                const distanceList = filters.distance || [];
                const newDistances = e.target.checked
                  ? [...distanceList, distance.key]
                  : distanceList.filter(d => d !== distance.key);
                onFilterChange({ ...filters, distance: newDistances });
              }}
              id={`filter-distance-${distance.key}`}
            />
          ))}
        </FormGroup>
      </FilterSection>

      {/* Fun Things to Do */}
      <FilterSection title="Fun things to do" defaultExpanded={false}>
        <FormGroup>
          {[
            { key: 'liveMusic', label: 'Live music/Performance' },
            { key: 'massage', label: 'Massage' },
            { key: 'bicycleRental', label: 'Bicycle rental' },
            { key: 'fitness', label: 'Fitness' },
            { key: 'fitnessCenter', label: 'Fitness center' }
          ].map(activity => (
            <FilterCheckbox
              key={activity.key}
              label={activity.label}
              count={getCount('funThings', activity.key)}
              checked={filters.funThings?.includes(activity.key) || false}
              onChange={(e) => {
                const funThings = filters.funThings || [];
                const newThings = e.target.checked
                  ? [...funThings, activity.key]
                  : funThings.filter(t => t !== activity.key);
                onFilterChange({ ...filters, funThings: newThings });
              }}
              id={`filter-fun-${activity.key}`}
            />
          ))}
        </FormGroup>
      </FilterSection>

      {/* Certifications */}
      <FilterSection title="Certifications" defaultExpanded={false}>
        <FormGroup>
          <FilterCheckbox
            label="Sustainability certification"
            count={getCount('certifications', 'sustainability')}
            checked={filters.certifications?.includes('sustainability') || false}
            onChange={(e) => {
              const certifications = filters.certifications || [];
              const newCerts = e.target.checked
                ? [...certifications, 'sustainability']
                : certifications.filter(c => c !== 'sustainability');
              onFilterChange({ ...filters, certifications: newCerts });
            }}
            id="filter-sustainability"
          />
        </FormGroup>
      </FilterSection>

      {/* Property Accessibility */}
      <FilterSection title="Property accessibility" defaultExpanded={false}>
        <FormGroup>
          {[
            { key: 'auditoryGuidance', label: 'Auditory guidance' },
            { key: 'visualAidsTactile', label: 'Visual aids (tactile signs)' },
            { key: 'visualAidsBraille', label: 'Visual aids (Braille)' },
            { key: 'bathroomEmergencyCord', label: 'Bathroom emergency cord' },
            { key: 'raisedToilet', label: 'Raised toilet' },
            { key: 'toiletGrabRails', label: 'Toilet with grab rails' },
            { key: 'loweredSink', label: 'Lowered sink' }
          ].map(accessibility => (
            <FilterCheckbox
              key={accessibility.key}
              label={accessibility.label}
              count={getCount('propertyAccessibility', accessibility.key)}
              checked={filters.propertyAccessibility?.[accessibility.key] || false}
              onChange={(e) => {
                onFilterChange({
                  ...filters,
                  propertyAccessibility: {
                    ...filters.propertyAccessibility,
                    [accessibility.key]: e.target.checked
                  }
                });
              }}
              id={`filter-property-access-${accessibility.key}`}
            />
          ))}
        </FormGroup>
      </FilterSection>

      {/* Room Accessibility */}
      <FilterSection title="Room accessibility" defaultExpanded={false}>
        <FormGroup>
          {[
            { key: 'emergencyCordBathroom', label: 'Emergency cord in bathroom' },
            { key: 'higherLevelToilet', label: 'Higher level toilet' },
            { key: 'elevatorAccess', label: 'Upper floors accessible by elevator' },
            { key: 'showerChair', label: 'Shower chair' },
            { key: 'rollInShower', label: 'Roll in shower' },
            { key: 'lowerSink', label: 'Lower sink' },
            { key: 'toiletGrabRails', label: 'Toilet with grab rails' },
            { key: 'raisedToilet', label: 'Raised toilet' },
            { key: 'adaptedBath', label: 'Adapted bath' },
            { key: 'wheelchairAccessible', label: 'Entire unit wheelchair accessible' },
            { key: 'groundFloor', label: 'Entire unit located on ground floor' }
          ].map(accessibility => (
            <FilterCheckbox
              key={accessibility.key}
              label={accessibility.label}
              count={getCount('roomAccessibility', accessibility.key)}
              checked={filters.roomAccessibility?.[accessibility.key] || false}
              onChange={(e) => {
                onFilterChange({
                  ...filters,
                  roomAccessibility: {
                    ...filters.roomAccessibility,
                    [accessibility.key]: e.target.checked
                  }
                });
              }}
              id={`filter-room-access-${accessibility.key}`}
            />
          ))}
        </FormGroup>
      </FilterSection>
    </aside>
  );
};

export default FiltersSidebar;
